import {
  currencyRateConversion,
  formatAmount,
  isAutoload,
} from "../../../utils/hooks/dataHandler";
import {
  autoTextColor,
  beautyAutoLine,
  boldAutoColumn,
  buildNotesWithCurrencyRate,
} from "../../../utils/pdf/page";
import MainComputationCalculator from "../../../builders/calculator/mainComputation";
import {
  buildAutoNote,
  buildAutoTable,
  enhanceReferenceColumn,
  formatReferenceDetails,
} from "../../../utils/pdf/buildAutoTable";
import { BasePdfGenerator } from "../../../utils/pdf/BasePdfGenerator";
import { PDFLandscapeScheduleConstants } from "../../../utils/static/pdfConstants";
import { createMainComputationSgdColumn } from "./sgdColumn";
import {
  buildAgreementLabel,
  calculateAutoloadTaxSetOff,
  createExchangeRateResolver,
  DEFAULT_UNABSORBED_SUMMARY,
  getActiveUnutilisedRows,
  getManualTaxPreviouslyAssessedRows,
  getUnutilisedAmountLabel,
  isAutoloadTaxPreviouslyAssessedRow,
  isDeductAdjustmentRow,
} from "./shared";

const getUnutilisedLossLabel = (row) => {
  if (isAutoload(row?.status)) {
    return row.name || row.schedule || "Unutilised loss";
  }

  if (row?.date) {
    const date = new Date(row.date);
    if (!Number.isNaN(date.getTime()) && date.getFullYear() !== 1970) {
      return `Financial year ended ${date.getFullYear()}`;
    }
  }

  return row?.yearOfAssessment
    ? `Financial year ended ${row.yearOfAssessment}`
    : "Unutilised loss";
};

const generateSingleYA = (
  forView,
  company,
  currency,
  currencyRate,
  netProfitBeforeTax,
  referenceData,
  unutilisedCapitalAllowances,
  unutilisedLosses,
  currentYearLosses,
  unutilisedDonations,
  corporateIncomeTaxRebate,
  currentYearRebate,
  isStartup,
  taxPreviouslyAssessed,
  computedSummary,
  unabsorbedSummary = DEFAULT_UNABSORBED_SUMMARY,
  comments,
  reviewNote,
  isExcel = false,
  scheduleName = "Main Tax Computation"
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let totalLines = [];
  let totalSubLines = [];
  let totalDoubleLines = [];

  const currencyView = currency ?? company.currency;
  const isNonSgdCurrency = currencyView !== "SGD";
  const exchangeRate = currencyRate ?? company.currencyRate ?? 1;
  const isInvestmentCompany = company?.companyType === "investment";
  const getExchangeRate = createExchangeRateResolver(exchangeRate);

  let headers = [
    "",
    boldAutoColumn(currencyView),
    boldAutoColumn(currencyView),
    boldAutoColumn("Sch"),
  ];

  if (forView)
    headers.push({
      content: "Reference",
      styles: { textColor: [255, 0, 0], fontStyle: "bold" },
    });

  const contentData = [];
  const sgdAmountOverrides = new WeakMap();
  let sgdColumnEndLine = -1;
  const {
    addSgdColumnThroughRow: addSgdColumnThroughContentData,
    addSgdHeader,
  } = createMainComputationSgdColumn({
    currencyRate: exchangeRate,
    getRowExchangeRate: (row) => row[forView ? 5 : 4],
    sgdAmountOverrides,
    unutilisedCapitalAllowances,
    unutilisedDonations,
    unutilisedLosses,
  });

  const addSgdColumnThroughRow = (endRowIndex) => {
    if (!isNonSgdCurrency || endRowIndex < 0) return;

    addSgdHeader(headers);
    addSgdColumnThroughContentData(contentData, endRowIndex);
    sgdColumnEndLine = endRowIndex;
  };

  if (!isInvestmentCompany)
    contentData.push([
      boldAutoColumn("Net Profit / (Loss) before Tax"),
      "",
      formatAmount(netProfitBeforeTax),
      "P&L",
    ]);

  const taxAdjustmentGeneral = referenceData["taxAdjustments"] || [];
  const taxAdjustmentSeparateSourcedIncome =
    referenceData["taxAdjustmentSeparateSourcedIncome"] || [];
  const taxAdjustmentBalancingCharge =
    referenceData["taxAdjustmentBalancingCharge"] || [];
  let taxAdjustments = [
    ...taxAdjustmentGeneral,
    ...taxAdjustmentSeparateSourcedIncome,
    ...taxAdjustmentBalancingCharge,
  ];
  const furtherDeductionS14n = referenceData["furtherDeductionS14n"] || [];
  const furtherDeductionOther = referenceData["furtherDeductionOther"] || [];
  let furtherDeductionRows = isInvestmentCompany
    ? []
    : [...furtherDeductionS14n, ...furtherDeductionOther];
  let otherSourceIncome = isInvestmentCompany
    ? []
    : referenceData["otherSourceIncome"];
  let unutilisedDonation = isInvestmentCompany ? [] : unutilisedDonations;
  let currentDonation = referenceData["currentYearDonations"];
  let unutilisedCA = isInvestmentCompany ? [] : unutilisedCapitalAllowances;
  let currentCA = isInvestmentCompany
    ? []
    : referenceData["currentYearCapitalAllowances"];

  let adjustedProfitAfterTaxAdjustments = netProfitBeforeTax;
  let adjustedProfitBeforeCapitalAllowances = adjustedProfitAfterTaxAdjustments;

  let currentYearCa = 0;
  let hasTaxAdjustment = taxAdjustments && taxAdjustments.length > 0;
  let hasFurtherDeductionRows =
    !isInvestmentCompany &&
    furtherDeductionRows &&
    furtherDeductionRows.length > 0;

  const pushTaxAdjustmentRows = (rows) => {
    rows.forEach((row) => {
      let content = [
        row.name,
        isDeductAdjustmentRow(row)
          ? formatAmount(row.amount, true, true)
          : formatAmount(Math.abs(row.amount)),
        "",
        row.schedule && `${row.schedule}`,
      ];

      if (forView) content.push(autoTextColor(row.reference));

      contentData.push(content);
    });
  };

  const pushTaxAdjustmentSubsection = (
    title,
    rows,
    shouldSplitByAdjustment = true
  ) => {
    if (!rows || rows.length === 0) return;

    if (title) contentData.push([boldAutoColumn(title)]);

    if (!shouldSplitByAdjustment) {
      pushTaxAdjustmentRows(rows);
      totalLines.push(contentData.length - 1);

      let total = MainComputationCalculator.calTaxAdjustments(rows);
      contentData.push(["", "", formatAmount(total)]);
      totalSubLines.push(contentData.length - 1);
      return;
    }

    const addRows = rows.filter((row) => !isDeductAdjustmentRow(row));
    const lessRows = rows.filter((row) => isDeductAdjustmentRow(row));

    if (addRows.length > 0) {
      contentData.push([boldAutoColumn("Add: Non-deductible items")]);
      pushTaxAdjustmentRows(addRows);
      totalLines.push(contentData.length - 1);

      let totalAdd = MainComputationCalculator.calTaxAdjustments(addRows);
      contentData.push(["", "", formatAmount(totalAdd)]);
      totalSubLines.push(contentData.length - 1);
    }

    if (lessRows.length > 0) {
      contentData.push([
        boldAutoColumn("Less: Deductible / non-taxable items"),
      ]);
      pushTaxAdjustmentRows(lessRows);
      totalLines.push(contentData.length - 1);

      let totalLess = MainComputationCalculator.calTaxAdjustments(lessRows);
      contentData.push(["", "", formatAmount(totalLess)]);
      totalSubLines.push(contentData.length - 1);
    }
  };

  const pushFurtherDeductionSubsection = (title, rows) => {
    if (!rows || rows.length === 0) return;

    contentData.push([boldAutoColumn(title)]);

    rows.forEach((row) => {
      let content = [
        row.name,
        row.taxAdjustment === "Deduct"
          ? formatAmount(row.amount, true, true)
          : formatAmount(Math.abs(row.amount)),
        "",
        row.schedule && `${row.schedule}`,
      ];
      if (forView) content.push(autoTextColor(row.reference));

      contentData.push(content);
    });
    totalLines.push(contentData.length - 1);

    let totalFurtherDeduction =
      MainComputationCalculator.calTaxAdjustments(rows);
    contentData.push(["", "", formatAmount(totalFurtherDeduction)]);
    totalSubLines.push(contentData.length - 1);
  };

  if (hasTaxAdjustment) {
    contentData.push([]);

    if (isInvestmentCompany) {
      contentData.push([boldAutoColumn("Net Investment Income")]);

      taxAdjustments.map((row) => {
        let content = [
          row.name,
          row.taxAdjustment === "Deduct"
            ? formatAmount(row.amount, true, true)
            : formatAmount(Math.abs(row.amount)),
          "",
          row.schedule && `${row.schedule}`,
        ];

        if (forView) content.push(autoTextColor(row.reference));

        contentData.push(content);
      });
    } else {
      pushTaxAdjustmentSubsection("Tax Adjustments", taxAdjustmentGeneral);

      if (
        taxAdjustmentGeneral.length > 0 &&
        taxAdjustmentSeparateSourcedIncome.length > 0
      ) {
        contentData.push([]);
      }

      pushTaxAdjustmentSubsection(
        "Add/(Less): Other sourced income / (expenses)",
        taxAdjustmentSeparateSourcedIncome,
        false
      );

      if (
        (taxAdjustmentGeneral.length > 0 ||
          taxAdjustmentSeparateSourcedIncome.length > 0) &&
        taxAdjustmentBalancingCharge.length > 0
      ) {
        contentData.push([]);
      }

      pushTaxAdjustmentSubsection("", taxAdjustmentBalancingCharge, false);
    }

    let totalTaxAdjustments =
      MainComputationCalculator.calTaxAdjustments(taxAdjustments);
    // contentData.push(["", "", formatAmount(totalTaxAdjustments)]);

    adjustedProfitAfterTaxAdjustments += totalTaxAdjustments;

    if (isInvestmentCompany) {
      totalLines.push(contentData.length - 1);
      contentData.push([
        boldAutoColumn("Total Investment Income"),
        "",
        formatAmount(adjustedProfitAfterTaxAdjustments),
      ]);

      adjustedProfitBeforeCapitalAllowances = adjustedProfitAfterTaxAdjustments;
    } else {
      // Original logic for common companies
      const onlyTaxAdjustments =
        [
          furtherDeductionRows,
          otherSourceIncome,
          unutilisedDonation,
          currentDonation,
          unutilisedCA,
          currentCA,
          unutilisedLosses,
        ].every((item) => Array.isArray(item) && item.length === 0) &&
        !(currentYearLosses && currentYearLosses === null);

      contentData.push([
        onlyTaxAdjustments ||
        (adjustedProfitAfterTaxAdjustments < 0 &&
          furtherDeductionRows.length === 0)
          ? boldAutoColumn(
              adjustedProfitAfterTaxAdjustments > 0
                ? "Chargeable Income Before Exempt Amount"
                : "Current Year Losses"
            )
          : "",
        "",
        formatAmount(adjustedProfitAfterTaxAdjustments),
      ]);

      adjustedProfitBeforeCapitalAllowances = adjustedProfitAfterTaxAdjustments;
      if (
        (onlyTaxAdjustments || !hasFurtherDeductionRows) &&
        adjustedProfitAfterTaxAdjustments < 0
      ) {
        if (adjustedProfitAfterTaxAdjustments < 0) {
          totalSubLines.push(contentData.length - 1);
          totalDoubleLines.push(contentData.length - 1);
        }
      }
    }
  }

  if (hasFurtherDeductionRows && !isInvestmentCompany) {
    contentData.push([boldAutoColumn("Less: Further Deduction")]);
    pushFurtherDeductionSubsection(
      "Further Deduction S14N",
      furtherDeductionS14n
    );
    pushFurtherDeductionSubsection(
      "Other Further Deduction",
      furtherDeductionOther
    );

    adjustedProfitBeforeCapitalAllowances =
      adjustedProfitAfterTaxAdjustments +
      MainComputationCalculator.calTaxAdjustments(furtherDeductionRows);

    contentData.push([
      boldAutoColumn(
        adjustedProfitBeforeCapitalAllowances > 0
          ? "Adjusted Profit Before Capital Allowances"
          : "Current Year Losses"
      ),
      "",
      formatAmount(adjustedProfitBeforeCapitalAllowances),
    ]);

    if (adjustedProfitBeforeCapitalAllowances < 0) {
      totalSubLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
    }
    contentData.push([]);
  }

  if (
    !isInvestmentCompany &&
    !hasTaxAdjustment &&
    !hasFurtherDeductionRows &&
    adjustedProfitBeforeCapitalAllowances < 0
  ) {
    contentData.push([]);
    totalSubLines.push(contentData.length - 1);
    contentData.push([
      boldAutoColumn(
        adjustedProfitBeforeCapitalAllowances > 0
          ? "Adjusted Profit Before Capital Allowances"
          : "Current Year Losses"
      ),
      "",
      formatAmount(adjustedProfitBeforeCapitalAllowances),
    ]);
    totalSubLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
  }

  const isYearLosses =
    !isInvestmentCompany && adjustedProfitBeforeCapitalAllowances < 0;
  let adjustedProfitAfterCapitalAllowances =
    adjustedProfitBeforeCapitalAllowances;

  const hasOtherIncome =
    !isInvestmentCompany && otherSourceIncome && otherSourceIncome.length > 0;
  const hasCapitalAllowanceRows =
    (!isInvestmentCompany && unutilisedCA && unutilisedCA.length > 0) ||
    (currentCA && currentCA.length > 0);
  const buildOtherIncome = () => {
    if (hasOtherIncome) {
      if (isYearLosses) contentData.push([]);

      contentData.push([
        boldAutoColumn(`${!isYearLosses ? "Add: " : ""}Other sourced income`),
      ]);

      otherSourceIncome.map((row) => {
        let content = [
          row.name,
          row.taxAdjustment === "Deduct"
            ? formatAmount(row.amount, true, true)
            : formatAmount(row.amount),
          "",
          row.schedule && `${row.schedule}`,
        ];
        if (forView) content.push(autoTextColor(row.reference));

        contentData.push(content);
      });
      totalLines.push(contentData.length - 1);

      let totalOtherSourceIncome = MainComputationCalculator.justArraySum(
        otherSourceIncome,
        false
      );

      contentData.push(["", "", formatAmount(totalOtherSourceIncome)]);

      if (isYearLosses)
        adjustedProfitAfterCapitalAllowances = totalOtherSourceIncome;

      if (
        isYearLosses &&
        !(
          (unutilisedCA && unutilisedCA.length > 0) ||
          (currentCA && currentCA.length > 0)
        )
      ) {
        totalSubLines.push(contentData.length - 1);
        contentData.push([
          adjustedProfitAfterCapitalAllowances > 0
            ? ""
            : boldAutoColumn("Unutilised CA c/f"),
          "",
          formatAmount(totalOtherSourceIncome),
        ]);

        if (adjustedProfitAfterCapitalAllowances < 0) {
          totalSubLines.push(contentData.length - 1);
          totalDoubleLines.push(contentData.length - 1);
        }
      }

      if (!isYearLosses) {
        adjustedProfitAfterCapitalAllowances += totalOtherSourceIncome;
        totalSubLines.push(contentData.length - 1);
        contentData.push([
          adjustedProfitAfterCapitalAllowances < 0
            ? boldAutoColumn("Unutilised CA c/f")
            : hasCapitalAllowanceRows
              ? boldAutoColumn(
                  "Adjusted Profit / (Loss) after Capital Allowances"
                )
              : "",
          "",
          formatAmount(adjustedProfitAfterCapitalAllowances),
        ]);

        if (adjustedProfitAfterCapitalAllowances < 0) {
          totalSubLines.push(contentData.length - 1);
          totalDoubleLines.push(contentData.length - 1);
        }
      }
    }
  };

  if (isYearLosses && !isInvestmentCompany) buildOtherIncome();

  if (hasCapitalAllowanceRows) {
    contentData.push([
      boldAutoColumn(
        `${
          !isYearLosses && adjustedProfitAfterCapitalAllowances > 0
            ? "Add/(Less): "
            : ""
        }Unutilised CA b/f`
      ),
    ]);

    unutilisedCA.map((row) => {
      let content = [
        row.yearOfAssessment == company.yearOfAssessment
          ? "Current Year CA"
          : "YA " + row.yearOfAssessment,
        getUnutilisedAmountLabel(row, () =>
          formatAmount(row.amount, true, true)
        ),
        "",
        row.schedule && `${row.schedule}`,
      ];
      if (forView) content.push(autoTextColor(row.reference));
      content.push(row.exchangeRate);

      contentData.push(content);
    });

    currentCA.map((row) => {
      let content = [
        row.name,
        row.row_id && row.row_id === "ca-summary-00"
          ? formatAmount(row.amount)
          : row.taxAdjustment === "Deduct"
            ? formatAmount(row.amount, true, true)
            : formatAmount(Math.abs(row.amount)),
        "",
        row.schedule && `${row.schedule}`,
      ];
      if (forView) content.push(autoTextColor(row.reference));

      contentData.push(content);
    });
    totalLines.push(contentData.length - 1);

    currentYearCa = MainComputationCalculator.calTaxAdjustments(currentCA);
    const activeUnutilisedCA = getActiveUnutilisedRows(unutilisedCA);
    let totalUnutilisedCA = Math.round(
      currentYearCa -
        activeUnutilisedCA.reduce((sum, row) => sum + Math.abs(row.amount), 0)
    );

    const unutilisedCaSubtotalRow = ["", "", formatAmount(totalUnutilisedCA)];
    contentData.push(unutilisedCaSubtotalRow);
    sgdAmountOverrides.set(
      unutilisedCaSubtotalRow,
      currencyRateConversion(currentYearCa, getExchangeRate(), "SGD") -
        activeUnutilisedCA.reduce(
          (sum, row) =>
            sum +
            currencyRateConversion(
              Math.abs(parseFloat(row.amount) || 0),
              getExchangeRate(row.exchangeRate),
              "SGD"
            ),
          0
        )
    );
    totalSubLines.push(contentData.length - 1);

    if (isYearLosses && !hasOtherIncome)
      adjustedProfitAfterCapitalAllowances = totalUnutilisedCA;
    else adjustedProfitAfterCapitalAllowances += totalUnutilisedCA;

    contentData.push([
      boldAutoColumn(
        hasOtherIncome && !isYearLosses
          ? ""
          : isYearLosses ||
              (adjustedProfitAfterCapitalAllowances <= 0 && !hasOtherIncome)
            ? adjustedProfitAfterCapitalAllowances > 0
              ? hasOtherIncome
                ? isYearLosses
                  ? "Unutilised CA c/f"
                  : "Adjusted Profit / (Loss) after Capital Allowances"
                : ""
              : "Unutilised CA c/f"
            : !(unutilisedLosses && unutilisedLosses.length > 0) &&
                !(
                  (unutilisedDonation && unutilisedDonation.length > 0) ||
                  (currentDonation && currentDonation.length > 0) ||
                  hasOtherIncome
                )
              ? "Chargeable Income Before Exempt Amount"
              : "Adjusted Profit / (Loss) after Capital Allowances"
      ),
      "",
      formatAmount(adjustedProfitAfterCapitalAllowances),
    ]);

    if (
      isYearLosses ||
      (adjustedProfitAfterCapitalAllowances <= 0 && !hasOtherIncome)
    ) {
      totalSubLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
    }
  }

  if (!isYearLosses && !isInvestmentCompany) buildOtherIncome();

  if (
    !isInvestmentCompany &&
    ((unutilisedLosses && unutilisedLosses.length > 0) ||
      (currentYearLosses && currentYearLosses !== null))
  ) {
    if (isYearLosses) contentData.push([]);

    contentData.push([
      boldAutoColumn(
        `${
          !isYearLosses && adjustedProfitAfterCapitalAllowances > 0
            ? "Less: "
            : ""
        }Unutilised losses b/f`
      ),
    ]);

    let unutilisedLossesWithoutCurrentYearLosses = unutilisedLosses.filter(
      (loss) =>
        loss.reference !== "[Auto Generated] Current Year Losses" &&
        !isAutoload(loss?.status)
    );

    if (
      unutilisedLossesWithoutCurrentYearLosses &&
      unutilisedLossesWithoutCurrentYearLosses.length > 0
    )
      unutilisedLossesWithoutCurrentYearLosses.map((row) => {
        let content = [
          getUnutilisedLossLabel(row),
          getUnutilisedAmountLabel(row, () =>
            formatAmount(row.amount, true, true, true)
          ),
          "",
          "",
        ];
        if (forView) content.push(autoTextColor(row.reference));
        content.push(row.exchangeRate);

        contentData.push(content);
      });

    const autoloadedLossesData = unutilisedLosses.filter((loss) =>
      isAutoload(loss?.status)
    );
    if (autoloadedLossesData && autoloadedLossesData.length > 0) {
      autoloadedLossesData.map((row) => {
        let content = [
          getUnutilisedLossLabel(row),
          getUnutilisedAmountLabel(row, () =>
            formatAmount(row.amount, true, true, true)
          ),
          "",
          row.schedule && `${row.schedule}`,
        ];
        if (forView) content.push(autoTextColor(row.reference));
        content.push(row.exchangeRate);

        contentData.push(content);
      });
    }

    if (isYearLosses) {
      contentData.push([
        "Current Year Losses",
        formatAmount(adjustedProfitBeforeCapitalAllowances),
        "",
        "",
        "",
      ]);
    }
    if (
      (unutilisedLossesWithoutCurrentYearLosses &&
        unutilisedLossesWithoutCurrentYearLosses.length > 0) ||
      isYearLosses
    )
      totalLines.push(contentData.length - 1);

    let totalUnutilisedLosses =
      Math.abs(
        Math.round(
          getActiveUnutilisedRows(unutilisedLosses).reduce(
            (sum, row) => sum + Math.abs(row.amount),
            0
          )
        )
      ) + Math.abs(isYearLosses ? adjustedProfitBeforeCapitalAllowances : 0);

    const unutilisedLossesSubtotalRow = [
      "",
      "",
      formatAmount(-totalUnutilisedLosses),
    ];
    contentData.push(unutilisedLossesSubtotalRow);
    sgdAmountOverrides.set(
      unutilisedLossesSubtotalRow,
      -(
        getActiveUnutilisedRows(unutilisedLosses).reduce(
          (sum, row) =>
            sum +
            currencyRateConversion(
              Math.abs(parseFloat(row.amount) || 0),
              getExchangeRate(row.exchangeRate),
              "SGD"
            ),
          0
        ) +
        currencyRateConversion(
          Math.abs(isYearLosses ? adjustedProfitBeforeCapitalAllowances : 0),
          getExchangeRate(),
          "SGD"
        )
      )
    );
    totalSubLines.push(contentData.length - 1);

    if (isYearLosses) {
      let totalAmount = -totalUnutilisedLosses;
      if (adjustedProfitAfterCapitalAllowances > 0) {
        adjustedProfitAfterCapitalAllowances -= totalUnutilisedLosses;
        if (!isYearLosses) totalAmount = adjustedProfitAfterCapitalAllowances;
      }
      contentData.push([
        boldAutoColumn(totalAmount <= 0 ? "Unutilised losses c/f" : ""),
        "",
        formatAmount(totalAmount),
      ]);
      if (totalAmount <= 0) {
        totalSubLines.push(contentData.length - 1);
        totalDoubleLines.push(contentData.length - 1);
      }
    } else {
      let totalAmount = -totalUnutilisedLosses;
      if (adjustedProfitAfterCapitalAllowances > 0) {
        adjustedProfitAfterCapitalAllowances -= totalUnutilisedLosses;
        totalAmount = adjustedProfitAfterCapitalAllowances;
      }
      contentData.push([
        boldAutoColumn(
          totalAmount < 0
            ? "Unutilised losses c/f"
            : !(
                  (unutilisedDonation && unutilisedDonation.length > 0) ||
                  (currentDonation && currentDonation.length > 0)
                )
              ? "Chargeable Income Before Exempt Amount"
              : "Adjusted Profit / (Loss) after CA and Unutilised Losses b/f"
        ),
        "",
        formatAmount(totalAmount),
      ]);

      if (totalAmount < 0) {
        totalSubLines.push(contentData.length - 1);
        totalDoubleLines.push(contentData.length - 1);
      }
    }
  }

  if (
    (unutilisedDonation && unutilisedDonation.length > 0) ||
    (currentDonation && currentDonation.length > 0)
  ) {
    contentData.push([]);

    if (isInvestmentCompany) {
      // For investment companies, only show Current Year Donations (no unutilised)
      contentData.push([boldAutoColumn("Less: Current Year Donations")]);
    } else {
      contentData.push([
        boldAutoColumn(
          `${
            adjustedProfitAfterCapitalAllowances > 0 && !isYearLosses
              ? "Less: "
              : ""
          }Unutilised donations b/f`
        ),
      ]);
    }

    // Only show unutilised donations for common companies
    if (!isInvestmentCompany) {
      unutilisedDonation.map((row) => {
        let content = [
          "Financial year ended " + new Date(row.date).getFullYear(),
          getUnutilisedAmountLabel(row, () =>
            formatAmount(row.amount, true, true)
          ),
          "",
          row.schedule && `${row.schedule}`,
        ];
        if (forView) content.push(autoTextColor(row.reference));
        content.push(row.exchangeRate);

        contentData.push(content);
      });
    }

    currentDonation.map((row) => {
      let content = [
        row.name,
        row.taxAdjustment === "Deduct"
          ? formatAmount(row.amount, true, true)
          : formatAmount(Math.abs(row.amount)),
        "",
        row.schedule && `${row.schedule}`,
      ];
      if (forView) content.push(autoTextColor(row.reference));

      contentData.push(content);
    });

    totalLines.push(contentData.length - 1);

    let totalUnutilisedDonation = isInvestmentCompany
      ? 0
      : MainComputationCalculator.justArraySum(
          getActiveUnutilisedRows(unutilisedDonation),
          true
        );
    let totalCurrentDonation =
      MainComputationCalculator.calTaxAdjustments(currentDonation);
    let totalDonation = Math.round(
      totalUnutilisedDonation + totalCurrentDonation
    );

    const donationSubtotalRow = ["", "", formatAmount(totalDonation)];
    contentData.push(donationSubtotalRow);
    sgdAmountOverrides.set(
      donationSubtotalRow,
      currencyRateConversion(totalCurrentDonation, getExchangeRate(), "SGD") -
        getActiveUnutilisedRows(unutilisedDonation).reduce(
          (sum, row) =>
            sum +
            currencyRateConversion(
              parseFloat(row.amount) || 0,
              getExchangeRate(row.exchangeRate),
              "SGD"
            ),
          0
        )
    );
    totalSubLines.push(contentData.length - 1);

    if (isInvestmentCompany) {
      // Simplified flow for investment companies: Net Investment Income - Donations
      // totalDonation from calTaxAdjustments is already negative for deductions
      adjustedProfitAfterCapitalAllowances += totalDonation;
      let totalAmount = adjustedProfitAfterCapitalAllowances;

      contentData.push([
        boldAutoColumn("Chargeable Income Before Exempt Amount"),
        "",
        formatAmount(totalAmount),
      ]);
      totalSubLines.push(contentData.length - 1);
      if (totalAmount <= 0) {
        totalDoubleLines.push(contentData.length - 1);
      }
    } else if (isYearLosses) {
      let totalAmount = totalDonation;
      if (adjustedProfitAfterCapitalAllowances > 0) {
        adjustedProfitAfterCapitalAllowances += totalDonation;
        totalAmount = adjustedProfitAfterCapitalAllowances;
      }
      contentData.push([
        boldAutoColumn(
          adjustedProfitAfterCapitalAllowances <= 0
            ? "Unutilised Donations c/f"
            : "Chargeable Income Before Exempt Amount"
        ),
        "",
        formatAmount(totalAmount),
      ]);

      if (adjustedProfitAfterCapitalAllowances <= 0) {
        totalSubLines.push(contentData.length - 1);
        totalDoubleLines.push(contentData.length - 1);
      }
    } else {
      let totalAmount = totalDonation;
      if (adjustedProfitAfterCapitalAllowances > 0) {
        adjustedProfitAfterCapitalAllowances += totalDonation;
        totalAmount = adjustedProfitAfterCapitalAllowances;
      }
      contentData.push([
        boldAutoColumn(
          totalAmount < 0 || adjustedProfitAfterCapitalAllowances <= 0
            ? "Unutilised Donations c/f"
            : "Chargeable Income Before Exempt Amount"
        ),
        "",
        formatAmount(totalAmount),
      ]);

      if (totalAmount < 0 || adjustedProfitAfterCapitalAllowances <= 0) {
        totalSubLines.push(contentData.length - 1);
        totalDoubleLines.push(contentData.length - 1);
      }
    }
  }

  let chargebleIncomeBeforeExemptAmount = isInvestmentCompany
    ? adjustedProfitAfterCapitalAllowances
    : isYearLosses && adjustedProfitAfterCapitalAllowances < 0
      ? 0
      : adjustedProfitAfterCapitalAllowances;
  let chargebleIncomeAfterExemptAmount = chargebleIncomeBeforeExemptAmount;

  if (isNonSgdCurrency) {
    addSgdColumnThroughRow(contentData.length - 1);
  }

  const taxComputationHeaders = [
    "",
    boldAutoColumn("SGD"),
    boldAutoColumn("SGD"),
    boldAutoColumn("Sch"),
  ];

  if (forView)
    taxComputationHeaders.push({
      content: "Reference",
      styles: { textColor: [255, 0, 0], fontStyle: "bold" },
    });

  const taxComputationContentData = [];
  const taxTotalLines = [];
  const taxTotalSubLines = [];
  const taxTotalDoubleLines = [];

  if (isNonSgdCurrency && chargebleIncomeBeforeExemptAmount > 0) {
    totalSubLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
    chargebleIncomeBeforeExemptAmount =
      MainComputationCalculator.convertChargeableIncomeToSgd(
        chargebleIncomeBeforeExemptAmount,
        exchangeRate,
        [
          ...unutilisedCapitalAllowances,
          ...unutilisedLosses,
          ...unutilisedDonations,
        ]
      );
    chargebleIncomeBeforeExemptAmount = Math.round(
      chargebleIncomeBeforeExemptAmount
    );
    chargebleIncomeAfterExemptAmount = chargebleIncomeBeforeExemptAmount;
    taxComputationContentData.push([
      boldAutoColumn("Chargeable Income Before Exempt Amount"),
      "",
      formatAmount(chargebleIncomeBeforeExemptAmount),
    ]);
  }

  if (chargebleIncomeBeforeExemptAmount <= 0) {
    taxComputationContentData.push([]);
    taxComputationContentData.push([
      boldAutoColumn("Chargeable Income"),
      "",
      0,
    ]);
    taxTotalSubLines.push(taxComputationContentData.length - 1);
    taxTotalDoubleLines.push(taxComputationContentData.length - 1);
    taxComputationContentData.push([]);
  } else {
    taxComputationContentData.push([]);
    const startup = isStartup;
    let title = "Less: Partial Tax Exemption";
    if (startup) {
      title = "Less: Partial Tax Exemption Scheme for New Start-up Company";
    }
    taxComputationContentData.push([boldAutoColumn(title)]);

    const firstExemptAmountCondition = startup ? 100000 : 10000;
    let cappedFirstCentAmount = formatAmount(firstExemptAmountCondition);

    if (chargebleIncomeBeforeExemptAmount < firstExemptAmountCondition) {
      cappedFirstCentAmount = formatAmount(
        chargebleIncomeBeforeExemptAmount,
        true
      );
    }

    let firstExemptAmount = null;
    let secondExemptAmount = null;
    firstExemptAmount = startup
      ? Math.round(Math.min(chargebleIncomeBeforeExemptAmount, 100000) * 0.75)
      : Math.round(Math.min(chargebleIncomeBeforeExemptAmount, 10000) * 0.75);
    secondExemptAmount = Math.round(
      startup
        ? Math.min(
            Math.max(chargebleIncomeBeforeExemptAmount - 100000, 0),
            100000
          ) * 0.5
        : Math.min(
            Math.max(chargebleIncomeBeforeExemptAmount - 10000, 0),
            190000
          ) * 0.5
    );

    taxComputationContentData.push([
      `First S$${cappedFirstCentAmount} @ 75%`,
      formatAmount(-firstExemptAmount),
    ]);

    const secondExemptAmountCondition = startup ? 100000 : 190000;
    let cappedSecondCentAmount = formatAmount(secondExemptAmountCondition);
    if (secondExemptAmount > 0) {
      cappedSecondCentAmount = formatAmount(
        Math.min(
          secondExemptAmountCondition,
          Math.abs(
            chargebleIncomeBeforeExemptAmount - firstExemptAmountCondition
          )
        )
      );

      taxComputationContentData.push([
        `Next S$${cappedSecondCentAmount} @ 50%`,
        formatAmount(-secondExemptAmount),
      ]);
    }
    taxTotalLines.push(taxComputationContentData.length - 1);

    const totalExemptedAmount = firstExemptAmount + secondExemptAmount;
    taxComputationContentData.push([
      "",
      "",
      formatAmount(-totalExemptedAmount),
    ]);
    taxTotalSubLines.push(taxComputationContentData.length - 1);

    chargebleIncomeAfterExemptAmount -= totalExemptedAmount;
    taxComputationContentData.push([
      boldAutoColumn("Chargeable Income After Exempt Amount"),
      "",
      formatAmount(chargebleIncomeAfterExemptAmount),
    ]);
    taxTotalSubLines.push(taxComputationContentData.length - 1);
    taxTotalDoubleLines.push(taxComputationContentData.length - 1);
    taxComputationContentData.push([]);
  }

  const taxableIncome =
    chargebleIncomeBeforeExemptAmount > 0 &&
    chargebleIncomeAfterExemptAmount > 0
      ? (Math.round(chargebleIncomeAfterExemptAmount) * 0.17).toFixed(2)
      : 0;
  taxComputationContentData.push([
    boldAutoColumn("Tax Payable at 17%"),
    "",
    formatAmount(taxableIncome, false),
  ]);

  let totalTaxPreviouslyAssessed = calculateAutoloadTaxSetOff(
    taxPreviouslyAssessed
  );
  let checkValidCashRebate = false;

  let totalDeductions = Number(taxableIncome);
  const postDeductions =
    Number(taxableIncome) + Number(totalTaxPreviouslyAssessed);

  const manualPreviousTaxRows = getManualTaxPreviouslyAssessedRows(
    taxPreviouslyAssessed
  );
  let previousTax =
    manualPreviousTaxRows[manualPreviousTaxRows.length - 1] || {};
  let hasDeductions = taxPreviouslyAssessed.some(
    isAutoloadTaxPreviouslyAssessedRow
  );

  if (
    corporateIncomeTaxRebate.isRebate ||
    (corporateIncomeTaxRebate.taxableRate &&
      corporateIncomeTaxRebate.cappedAmount) ||
    hasDeductions
  ) {
    if (hasDeductions) {
      taxComputationContentData.push([]);
      taxComputationContentData.push([boldAutoColumn("Less: Tax deductions")]);
      taxPreviouslyAssessed.map((row) => {
        if (isAutoloadTaxPreviouslyAssessedRow(row))
          taxComputationContentData.push([
            `${row.name}`,
            row.taxAdjustment === "Deduct"
              ? formatAmount(Math.abs(row.amount), false, true)
              : formatAmount(Math.abs(row.amount), false),
            "",
            row.schedule && `${row.schedule}`,
          ]);
      });
      taxTotalLines.push(taxComputationContentData.length - 1);

      taxComputationContentData.push([
        "",
        "",
        formatAmount(totalTaxPreviouslyAssessed.toFixed(2), false),
      ]);
      taxTotalSubLines.push(taxComputationContentData.length - 1);
      taxComputationContentData.push([
        corporateIncomeTaxRebate.taxableRate &&
        corporateIncomeTaxRebate.cappedAmount
          ? boldAutoColumn("Tax Payable after Set Off")
          : "",
        "",
        formatAmount(postDeductions, false),
      ]);
    }

    let totalCorporateIncomeTaxRebate = 0;
    if (
      postDeductions > 0 &&
      corporateIncomeTaxRebate.taxableRate &&
      corporateIncomeTaxRebate.cappedAmount
    ) {
      totalCorporateIncomeTaxRebate =
        Math.round(
          Math.min(
            postDeductions * (corporateIncomeTaxRebate.taxableRate / 100),
            Number(corporateIncomeTaxRebate.cappedAmount)
          ) * 100
        ) / 100;

      if (
        !(
          currentYearRebate &&
          corporateIncomeTaxRebate &&
          corporateIncomeTaxRebate.isRebate &&
          Math.abs(totalCorporateIncomeTaxRebate) >
            Math.abs(currentYearRebate.amount)
        )
      ) {
        if (
          !currentYearRebate ||
          !corporateIncomeTaxRebate.isRebate ||
          Math.abs(totalCorporateIncomeTaxRebate) >
            Math.abs(currentYearRebate.amount)
        ) {
          if (totalTaxPreviouslyAssessed && taxPreviouslyAssessed.length > 0)
            taxComputationContentData.push([]);
          taxComputationContentData.push([
            `CIT rebate at ${
              corporateIncomeTaxRebate.taxableRate
            }%, capped at S$${formatAmount(
              corporateIncomeTaxRebate.cappedAmount
            )}`,
            "",
            formatAmount(-totalCorporateIncomeTaxRebate, false),
          ]);
        } else {
          if (
            Math.abs(totalCorporateIncomeTaxRebate) <
            Math.abs(currentYearRebate.amount)
          )
            checkValidCashRebate = true;
          totalCorporateIncomeTaxRebate = 0;
        }
      } else {
        taxComputationContentData.push([]);
        taxComputationContentData.push([boldAutoColumn("Less: Tax Rebate")]);

        taxComputationContentData.push([
          `CIT rebate at ${
            corporateIncomeTaxRebate.taxableRate
          }%, capped at S$${formatAmount(
            corporateIncomeTaxRebate.cappedAmount
          )}`,
          formatAmount(-totalCorporateIncomeTaxRebate, false),
        ]);
        taxComputationContentData.push([
          `CIT Rebate Cash Grant`,
          formatAmount(currentYearRebate.amount, false),
        ]);
        taxTotalLines.push(taxComputationContentData.length - 1);

        totalCorporateIncomeTaxRebate =
          Math.abs(totalCorporateIncomeTaxRebate) -
          Math.abs(currentYearRebate.amount);
        taxComputationContentData.push([
          "",
          "",
          formatAmount(-totalCorporateIncomeTaxRebate, false),
        ]);
        taxTotalSubLines.push(taxComputationContentData.length - 1);
        taxComputationContentData.push([
          boldAutoColumn("Tax payable before ECI"),
          "",
          formatAmount(
            postDeductions - Number(totalCorporateIncomeTaxRebate),
            false
          ),
        ]);
      }
    }
    totalDeductions = postDeductions - Number(totalCorporateIncomeTaxRebate);
  }

  if (previousTax.label) {
    taxComputationContentData.push([]);
    taxComputationContentData.push([
      previousTax.label,
      "",
      formatAmount(previousTax.amount, false),
    ]);
    if (!isExcel) taxComputationContentData.push([]);
  } else {
    previousTax = {
      label: "",
      amount: 0,
    };
  }

  taxTotalSubLines.push(taxComputationContentData.length - 1);

  const netTaxPayable = totalDeductions + previousTax.amount;
  taxComputationContentData.push([
    boldAutoColumn("Net Tax Payable / (Tax Refund)"),
    "",
    formatAmount(netTaxPayable, false),
  ]);
  taxTotalSubLines.push(taxComputationContentData.length - 1);
  taxTotalDoubleLines.push(taxComputationContentData.length - 1);

  const hasSgdColumn = sgdColumnEndLine >= 0;
  if (!hasSgdColumn && taxComputationContentData.length > 0) {
    const taxRowOffset = contentData.length;
    contentData.push(...taxComputationContentData);
    totalLines.push(...taxTotalLines.map((index) => taxRowOffset + index));
    totalSubLines.push(
      ...taxTotalSubLines.map((index) => taxRowOffset + index)
    );
    totalDoubleLines.push(
      ...taxTotalDoubleLines.map((index) => taxRowOffset + index)
    );
    taxComputationContentData.length = 0;
  }

  const refColumnIndex = hasSgdColumn ? 5 : 4;
  if (forView && contentData.length > 0) {
    const { body: enhancedBody, referenceDetails } = enhanceReferenceColumn(
      contentData,
      refColumnIndex,
      15
    );
    contentData.length = 0;
    contentData.push(...enhancedBody);
    const refDetailsText = formatReferenceDetails(referenceDetails);
    if (refDetailsText) {
      if (reviewNote !== "") reviewNote += "\n\n";
      reviewNote += "[Reference Details]\n" + refDetailsText;
    }
  }

  const taxRefColumnIndex = 4;
  if (forView && taxComputationContentData.length > 0) {
    const { body: enhancedBody, referenceDetails } = enhanceReferenceColumn(
      taxComputationContentData,
      taxRefColumnIndex,
      15
    );
    taxComputationContentData.length = 0;
    taxComputationContentData.push(...enhancedBody);
    const refDetailsText = formatReferenceDetails(referenceDetails);
    if (refDetailsText) {
      if (reviewNote !== "") reviewNote += "\n\n";
      reviewNote += "[Reference Details]\n" + refDetailsText;
    }
  }

  if (contentData.length > 0)
    buildAutoTable({
      head: [headers],
      body: contentData,
      pdf,
      customStartY: 55,
      schedule: {
        name: scheduleName,
      },
      company,
      columnStyles: hasSgdColumn
        ? {
            0: { halign: "left", minCellWidth: 70 },
            1: { halign: "right", valign: "bottom", cellWidth: 24 },
            2: { halign: "right", valign: "bottom", cellWidth: 24 },
            3: { halign: "right", valign: "bottom", cellWidth: 24 },
            4: { halign: "center", cellWidth: 17 },
            5: { halign: "center" },
          }
        : {
            0: { halign: "left", minCellWidth: 80 },
            1: { halign: "right", valign: "bottom", cellWidth: 30 },
            2: { halign: "right", valign: "bottom", cellWidth: 30 },
            3: { halign: "center", cellWidth: 19 },
            4: { halign: "center" },
          },
      didParseCell: function (data) {
        if (
          hasSgdColumn &&
          data.row.index > sgdColumnEndLine &&
          data.column.index === 3
        ) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (totalLines.includes(data.row.index) &&
            [1].includes(data.column.index)) ||
            (totalSubLines.includes(data.row.index) &&
              [2].includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            [2].includes(data.column.index)
        );
      },
      lineSettings: {
        totalLines: totalSubLines,
        totalDoubleLines,
        subTotalLines: totalLines,
        totalRows: [2],
        subTotalRows: [1],
      },
    });

  if (hasSgdColumn && taxComputationContentData.length > 0) {
    if (!pdf.isExcel) {
      pdf.doc.addPage();
      pdf.startY = 55;
    }

    buildAutoTable({
      head: [taxComputationHeaders],
      body: taxComputationContentData,
      pdf,
      customStartY: 55,
      schedule: {
        name: scheduleName,
      },
      company,
      columnStyles: {
        0: { halign: "left", cellWidth: 80 },
        1: { halign: "right", valign: "bottom", cellWidth: 30 },
        2: { halign: "right", valign: "bottom", cellWidth: 30 },
        3: { halign: "center", cellWidth: 19 },
        4: { halign: "center" },
      },
      didParseCell: function (data) {},
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (taxTotalLines.includes(data.row.index) &&
            [1].includes(data.column.index)) ||
            (taxTotalSubLines.includes(data.row.index) &&
              [2].includes(data.column.index)),
          taxTotalDoubleLines.includes(data.row.index) &&
            [2].includes(data.column.index)
        );
      },
      lineSettings: {
        totalLines: taxTotalSubLines,
        totalDoubleLines: taxTotalDoubleLines,
        subTotalLines: taxTotalLines,
        totalRows: [2],
        subTotalRows: [1],
      },
    });
  }

  comments = buildNotesWithCurrencyRate(company, comments);

  if (checkValidCashRebate)
    reviewNote +=
      "\n*CIT rebate is not applicable as the computed CIT rebate is less than the $2,000 CIT cash rebate received by the company.";

  buildAutoNote(
    pdf,
    forView,
    company,
    {
      name: scheduleName,
    },
    comments,
    reviewNote,
    [],
    false
  );

  // Landscape page: Unutilised items b/f and c/f summary (PDF only)
  const hasUnutilisedSummary =
    (!pdf.isExcel &&
      !isInvestmentCompany &&
      unabsorbedSummary.caRows.length > 0) ||
    unabsorbedSummary.lossRows.length > 0 ||
    unabsorbedSummary.donationRows.length > 0;

  if (hasUnutilisedSummary) {
    let totalSummaryLines = [];
    let totalSummaryDoubleLines = [];

    const lsc = PDFLandscapeScheduleConstants;
    if (!isExcel) pdf.doc.addPage("a4", "landscape");
    pdf.isLandscape = true;
    pdf.constants = lsc;
    pdf.rightX = lsc.rightX;

    const isNonSGD = currencyView !== "SGD";
    const hasAgreementColumns = [
      ...unabsorbedSummary.caRows,
      ...unabsorbedSummary.lossRows,
      ...unabsorbedSummary.donationRows,
    ].some((row) => row.toDeclareAgreement === true);

    const baseHeaders = [
      "",
      "Balance Available",
      "Current Year Utilisation",
      "Balance c/f",
    ];
    const headers = isNonSGD
      ? [
          ...baseHeaders,
          "Exchange Rate",
          "Balance Available",
          "Current Year Utilisation",
          "Balance c/f",
        ]
      : baseHeaders;

    const toSGD = (amount, rate = exchangeRate) =>
      Math.round(currencyRateConversion(amount, rate, "SGD"));

    const body = [];

    const currencyLabel = [
      "",
      boldAutoColumn(currencyView),
      boldAutoColumn(currencyView),
      boldAutoColumn(currencyView),
    ];

    if (isNonSGD)
      currencyLabel.push(
        "",
        boldAutoColumn("SGD"),
        boldAutoColumn("SGD"),
        boldAutoColumn("SGD")
      );

    body.push(currencyLabel);

    const disregardedLabel = "Disregarded";

    const pushSectionFromSummary = (typeLabel, rows) => {
      if (!rows || rows.length === 0) return;

      body.push([boldAutoColumn(typeLabel)]);

      let totalBf = 0;
      let totalUtil = 0;
      let totalCf = 0;
      let totalSgdBf = 0;
      let totalSgdUtil = 0;
      let totalSgdCf = 0;

      rows.forEach((row) => {
        const bf = Math.abs(parseFloat(row.amount) || 0);
        const util = row.utilisedThisYear || 0;
        const cf = parseFloat(row.balanceCf) || 0;
        const isForfeited = Boolean(row.isForfeited);

        if (!isForfeited) {
          totalBf += bf;
          totalUtil += Math.abs(util);
          totalCf += cf;

          if (isNonSGD) {
            const rate = getExchangeRate(row.exchangeRate);
            const sgdBf = toSGD(bf, rate);
            const sgdCf = toSGD(cf, rate);
            const sgdUtil = sgdCf - sgdBf;
            totalSgdBf += sgdBf;
            totalSgdUtil += sgdUtil;
            totalSgdCf += sgdCf;
          }
        }

        const rowData = [
          (typeLabel === "Capital Allowances"
            ? "YA " + row.yearOfAssessment
            : row.yearOfAssessment) + buildAgreementLabel(row),
          formatAmount(bf),
          isForfeited
            ? disregardedLabel
            : formatAmount(row.utilisedThisYear, true, true),
          isForfeited ? disregardedLabel : formatAmount(row.balanceCf),
        ];

        if (isNonSGD) {
          const rate = getExchangeRate(row.exchangeRate);
          const sgdBf = toSGD(bf, rate);

          if (isForfeited) {
            rowData.push(
              "",
              formatAmount(sgdBf),
              disregardedLabel,
              disregardedLabel
            );
          } else {
            const sgdCf = toSGD(row.balanceCf, rate);
            const sgdUtilised = sgdCf - sgdBf;
            rowData.push(
              `@ ${rate.toFixed(4)}`,
              formatAmount(sgdBf),
              formatAmount(sgdUtilised),
              formatAmount(sgdCf)
            );
          }
        }

        body.push(rowData);
      });

      if (totalBf !== 0 || totalUtil !== 0 || totalCf !== 0) {
        const totalRow = [
          boldAutoColumn("Total"),
          formatAmount(totalBf),
          formatAmount(totalUtil, true, true),
          formatAmount(totalCf),
        ];

        if (isNonSGD) {
          totalRow.push(
            "",
            formatAmount(totalSgdBf),
            formatAmount(totalSgdUtil, true, true),
            formatAmount(totalSgdCf)
          );
        }

        body.push(totalRow);
        totalSummaryLines.push(body.length - 2);
        totalSummaryLines.push(body.length - 1);
        totalSummaryDoubleLines.push(body.length - 1);

        if (typeLabel !== "Donations") body.push([]);
      }
    };

    pushSectionFromSummary("Capital Allowances", unabsorbedSummary.caRows);
    pushSectionFromSummary("Losses", unabsorbedSummary.lossRows);
    pushSectionFromSummary("Donations", unabsorbedSummary.donationRows);

    if (body.length > 0) {
      const payload = isNonSGD ? 1 : 2;
      const lineColumns = isNonSGD ? [1, 2, 3, 5, 6, 7] : [1, 2, 3];
      const colStyles = {
        0: { halign: "left", cellWidth: 40 * payload },
        1: { halign: "right", valign: "bottom", cellWidth: 32 * payload },
        2: { halign: "right", valign: "bottom", cellWidth: 32 * payload },
        3: { halign: "right", valign: "bottom", cellWidth: 32 * payload },
      };
      if (isNonSGD) {
        colStyles[4] = { halign: "center", cellWidth: 24 };
        colStyles[5] = { halign: "right", valign: "bottom", cellWidth: 32 };
        colStyles[6] = { halign: "right", valign: "bottom", cellWidth: 32 };
        colStyles[7] = { halign: "right", valign: "bottom", cellWidth: 32 };
      }

      buildAutoTable({
        pdf,
        customStartY: 55,
        schedule: {
          name: "Unutilised Items Brought Forward / Carried Forward",
          subTitle: "Statement A1",
        },
        head: [
          headers.map((h) => ({ content: h, styles: { fontStyle: "bold" } })),
        ],
        company,
        body,
        theme: "plain",
        styles: { fontSize: pdf.fs },
        headStyles: { fontStyle: "bold", halign: "center" },
        columnStyles: colStyles,
        didParseCell: function (data) {
          if (
            [0].includes(data.row.index) &&
            lineColumns.includes(data.column.index)
          ) {
            data.cell.styles.halign = "center";
          }
        },
        didDrawCell: function (data) {
          beautyAutoLine(
            data,
            pdf.doc,
            totalSummaryLines.includes(data.row.index) &&
              lineColumns.includes(data.column.index),
            totalSummaryDoubleLines.includes(data.row.index) &&
              lineColumns.includes(data.column.index)
          );
        },
        lineSettings: {
          totalLines: totalSummaryLines,
          totalDoubleLines: totalSummaryDoubleLines,
          totalRows: isNonSGD ? [1, 2, 3, 5, 6, 7] : [1, 2, 3],
        },
      });
    }
  }

  return pdf.getDoc(forView, company, { name: scheduleName });
};

export default generateSingleYA;
